import { useCallback, useEffect, useRef, useState } from 'react';

import MetaMaskOnboarding from '@metamask/onboarding';

type AccountsCollection = string[];

export function useMetaMask() {
    const onboarding = useRef<MetaMaskOnboarding>();

    const [accounts, setAccounts] = useState<AccountsCollection>([]);
    const [isMetaMaskConnected, setIsMetaMaskConnected] = useState<boolean>(false);
    const [isMetaMaskInstalled, setIsMetaMaskInstalled] = useState<boolean>(false);
    const [userHasEnoughFunds, setUserHasEnoughFunds] = useState<boolean | null>(null);

    useEffect(() => {
        if (!onboarding.current) {
            onboarding.current = new MetaMaskOnboarding();
        }
    }, []);

    useEffect(() => {
        if (MetaMaskOnboarding.isMetaMaskInstalled()) {
            setIsMetaMaskInstalled(true);

            if (accounts.length > 0) {
                setIsMetaMaskConnected(true);
                onboarding.current?.stopOnboarding();
            } else {
                setIsMetaMaskConnected(false);
            }
        } else {
            setIsMetaMaskInstalled(false);
            setIsMetaMaskConnected(false);
            onboarding.current?.stopOnboarding();
        }
    }, [accounts]);

    useEffect(() => {
        let mounted = true;

        function handleNewAccounts(newAccounts: AccountsCollection) {
            if (mounted) setAccounts(newAccounts);
        }

        if (MetaMaskOnboarding.isMetaMaskInstalled()) {
            window.ethereum.request({ method: 'eth_accounts' }).then(handleNewAccounts);
            window.ethereum.on('accountsChanged', handleNewAccounts);

            return () => {
                window.ethereum.removeListener('accountsChanged', handleNewAccounts);
                mounted = false;
            };
        }
    }, []);

    const connect = useCallback(() => {
        if (isMetaMaskInstalled) {
            window.ethereum
                .request({ method: 'eth_requestAccounts' })
                .then((newAccounts: AccountsCollection) => setAccounts(newAccounts));
        } else {
            onboarding.current?.startOnboarding();
        }
    }, [isMetaMaskInstalled]);

    useEffect(() => {
        let mounted = true;

        if (window && window.ethereum && accounts?.length > 0) {
            window.ethereum
                .request({ method: 'eth_getBalance', params: [accounts[0], 'latest'] })
                .then((balance = '0') => {
                    if (mounted) setUserHasEnoughFunds(parseInt(balance) > 0);
                })
                .catch((error: unknown) => {
                    if (error) {
                        // TODO: Add this case to Sentry (or other error monitoring tool).
                    }
                });
        }

        return () => {
            mounted = false;
        };
    }, [accounts]);

    return {
        accounts,
        connect,
        onboarding,
        isMetaMaskConnected,
        isMetaMaskInstalled,
        userHasEnoughFunds,
    };
}
